import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowUpRight, ArrowRight, Check, MessageCircle, Menu, X,
  Calculator, FlaskConical, BookText, Languages, TrendingUp, Leaf, Briefcase, Globe,
  Facebook, Youtube, Mail, Phone,
} from 'lucide-react';
import { useLenis } from '../lib/useLenis';
import { ApcMark } from '../components/ApcMark';
import { WHATSAPP_SIGNUP_URL, WHATSAPP_URL, CONTACT_PHONE_DISPLAY, CONTACT_EMAIL, SOCIAL } from '../lib/constants';

const easeOut = [0.2, 0, 0.1, 1];
const fade = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeOut } } };

// ─────────────────────────── MASKED LINE REVEAL ───────────────────────────
const RevealLines = ({ lines, delay = 0, className = '' }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span className="reveal-wrap block" key={i}>
        <motion.span
          className="reveal-line"
          initial={{ y: '110%' }}
          animate={{ y: '0%' }}
          transition={{ duration: 0.95, ease: easeOut, delay: delay + i * 0.12 }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// ─────────────────────────── NAV ───────────────────────────
const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h);
  }, []);
  const links = [
    { href: '#offering', label: 'Offering' },
    { href: '#tutors', label: 'Faculty' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#faq', label: 'FAQ' },
  ];
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-black/5' : 'bg-transparent'
      }`}
      data-testid="landing-header"
    >
      <div className="container-apc flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-logo">
          <ApcMark size={34} className="transition-transform group-hover:rotate-6" />
          <span className="font-bold text-[#1B2A41] tracking-tight hidden sm:block" style={{ fontFamily: 'Outfit' }}>
            Accounting Prep Club
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-[#1B2A41]/80 hover:text-[#1F6F4A] font-medium transition-colors">{l.label}</a>
          ))}
          <Link to="/register" className="btn-pill btn-primary" data-testid="nav-register-cta">
            Register for 2027 <ArrowUpRight size={16} />
          </Link>
        </nav>
        <button
          className="lg:hidden p-2 text-[#1B2A41]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-black/5"
          >
            <nav className="container-apc py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-3 px-3 rounded-xl hover:bg-[#F3F5F2] text-[#1B2A41] font-medium">
                  {l.label}
                </a>
              ))}
              <Link to="/register" className="btn-pill btn-primary mt-2 justify-center" onClick={() => setOpen(false)} data-testid="mobile-register-cta">
                Register for 2027 <ArrowUpRight size={16} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// ─────────────────────────── HERO ───────────────────────────
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  return (
    <section ref={ref} className="relative pt-28 md:pt-36 pb-12 md:pb-20 overflow-hidden" data-testid="hero-section">
      {/* background sweep */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -right-20 w-[600px] h-[600px] rounded-full bg-[#1F6F4A]/10 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-[500px] h-[500px] rounded-full bg-[#D4A017]/15 blur-3xl" />
      </div>

      <div className="container-apc">
        <motion.div className="overline mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <span className="inline-flex items-center gap-2">
            <span className="w-6 h-px bg-[#D4A017]" /> 2027 Academic Intake · CAPS & IEB
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-end">
          <div className="lg:col-span-7">
            <h1 className="text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-black text-[#1B2A41] leading-[0.98] tracking-tight">
              <RevealLines lines={['Affordable', 'online tutoring']} />
              <span className="block mt-2">
                <RevealLines lines={[<><span className="font-editorial font-normal text-[#1F6F4A]">for Grades</span></>]} delay={0.3} />{' '}
                <RevealLines lines={['4 — 12.']} delay={0.4} className="text-[#1F6F4A]" />
              </span>
            </h1>

            <motion.p
              variants={fade} initial="hidden" animate="show" transition={{ delay: 1.1 }}
              className="mt-8 max-w-xl text-base md:text-lg text-[#4B5563] leading-relaxed"
            >
              Live online classes for South African learners. CAPS and IEB tuition in Mathematics,
              Sciences, Accounting, Languages and more — from <span className="font-semibold text-[#1B2A41]">R99 per subject / month</span>.
            </motion.p>

            <motion.div
              variants={fade} initial="hidden" animate="show" transition={{ delay: 1.3 }}
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center"
            >
              <Link to="/register" className="btn-pill btn-primary" data-testid="hero-register-cta">
                Register for 2027 <ArrowRight size={18} />
              </Link>
              <a href={WHATSAPP_SIGNUP_URL} target="_blank" rel="noopener noreferrer" className="btn-pill btn-outline" data-testid="hero-whatsapp-cta">
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
            </motion.div>

            <motion.div
              variants={fade} initial="hidden" animate="show" transition={{ delay: 1.45 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#4B5563]"
            >
              <span className="flex items-center gap-2"><Check size={14} className="text-[#1F6F4A]"/> Live online lessons</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#1F6F4A]"/> CAPS & IEB streams</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#1F6F4A]"/> No long-term contracts</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <motion.div style={{ y: imageY, scale }} className="spotlight-img aspect-[4/5] max-w-md mx-auto lg:ml-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_exam-results-boost/artifacts/qqqfc5bd_unnamed%20%282%29.jpg"
                  alt="APC tutor delivering a live online lesson"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }}
                className="absolute -bottom-5 -left-4 md:-left-10 bg-white rounded-2xl shadow-xl px-4 py-3 border border-black/5"
              >
                <p className="text-[11px] font-mono tracking-wider text-[#4B5563]">LIVE SESSION</p>
                <p className="font-bold text-[#1B2A41]">Grade 12 Accounting</p>
                <p className="text-xs text-[#4B5563] mt-0.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"/> On air now
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
                className="absolute -top-4 -right-2 md:-right-6 bg-[#1F6F4A] text-white rounded-2xl px-4 py-3 shadow-xl"
              >
                <p className="text-[11px] font-mono tracking-wider opacity-80">FROM</p>
                <p className="text-2xl font-black">R99</p>
                <p className="text-xs opacity-80 -mt-0.5">per subject / mo</p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────── MARQUEE ───────────────────────────
const Marquee = () => {
  const words = ['Mathematics', 'Accounting', 'Physical Sciences', 'Life Sciences', 'Economics',
    'Business Studies', 'English HL', 'Afrikaans FAL', 'Mathematical Literacy', 'Natural Sciences', 'EMS'];
  const row = [...words, ...words];
  return (
    <section className="py-12 md:py-16 border-y border-black/5 bg-[#1B2A41] overflow-hidden" data-testid="marquee">
      <div className="marquee-track whitespace-nowrap">
        {row.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-6 px-6">
            <span className="font-editorial text-5xl md:text-7xl text-white/90">{w}</span>
            <span className="w-2.5 h-2.5 rounded-full bg-[#D4A017]" />
          </span>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────── OFFERING (CAPS vs IEB tabs) ───────────────────────────
const Offering = () => {
  const [tab, setTab] = useState('CAPS');
  const capsBlocks = [
    { band: 'Grades 4 – 7', price: 'R99', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'English HL', 'Afrikaans FAL'] },
    { band: 'Grades 8 – 9', price: 'R99', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'English HL', 'Afrikaans FAL', 'Natural Sciences', 'EMS'] },
    { band: 'Grades 10 – 11', price: 'R99', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'Mathematical Literacy', 'Accounting', 'Physical Sciences', 'Life Sciences', 'Economics', 'Business Studies', 'English HL', 'Afrikaans FAL'] },
    { band: 'Grade 12 · Matric', price: 'R199', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'Mathematical Literacy', 'Accounting', 'Physical Sciences', 'Life Sciences', 'Economics', 'Business Studies', 'English HL', 'Afrikaans FAL'], highlight: true },
  ];
  const iebBlocks = [
    { band: 'Grades 8 – 9', price: 'R249', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'English HL', 'Afrikaans FAL'] },
    { band: 'Grades 10 – 12', price: 'R249', freq: '2 live sessions / week · per subject', subjects: ['Mathematics', 'English HL', 'Afrikaans FAL', 'Physical Sciences', 'Business Studies'], highlight: true },
  ];
  const blocks = tab === 'CAPS' ? capsBlocks : iebBlocks;

  return (
    <section id="offering" className="section-pad bg-[#FAF9F6]" data-testid="offering-section">
      <div className="container-apc">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="overline mb-3">The 2027 Catalogue</div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A41] tracking-tight">
              Two curricula.<br/>
              <span className="font-editorial font-normal text-[#1F6F4A]">One</span> structured programme.
            </h2>
          </div>
          <div className="inline-flex p-1 bg-white rounded-full border border-black/5 shadow-sm self-start md:self-end" data-testid="curriculum-tabs">
            {['CAPS', 'IEB'].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t.toLowerCase()}`}
                onClick={() => setTab(t)}
                className={`relative px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  tab === t ? 'text-white' : 'text-[#1B2A41]'
                }`}
              >
                {tab === t && (
                  <motion.div
                    layoutId="tabPill"
                    className="absolute inset-0 bg-[#1F6F4A] rounded-full"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                  />
                )}
                <span className="relative z-10">{t}</span>
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {blocks.map((b) => (
            <div
              key={b.band}
              className={`rounded-3xl p-7 md:p-8 border transition-all hover:-translate-y-1 ${
                b.highlight
                  ? 'bg-[#1B2A41] text-white border-[#1B2A41]'
                  : 'bg-white text-[#1B2A41] border-black/5'
              }`}
              data-testid={`offering-block-${tab}-${b.band.replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <p className={`text-xs font-mono tracking-wider ${b.highlight ? 'text-[#D4A017]' : 'text-[#D4A017]'}`}>
                    {tab} · {b.band.toUpperCase()}
                  </p>
                  <h3 className="text-2xl md:text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit' }}>{b.band}</h3>
                </div>
                <div className="text-right">
                  <p className="text-3xl md:text-4xl font-black">{b.price}</p>
                  <p className={`text-xs ${b.highlight ? 'text-white/70' : 'text-[#4B5563]'}`}>per subject / mo</p>
                </div>
              </div>
              <p className={`text-sm ${b.highlight ? 'text-white/80' : 'text-[#4B5563]'} mb-5`}>{b.freq}</p>
              <div className="flex flex-wrap gap-2">
                {b.subjects.map((s) => (
                  <span
                    key={s}
                    className={`text-[13px] font-medium px-3 py-1.5 rounded-full border ${
                      b.highlight
                        ? 'border-white/15 bg-white/5 text-white'
                        : 'border-black/5 bg-[#F3F5F2] text-[#1B2A41]'
                    }`}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </motion.div>

        <div className="mt-8 text-sm text-[#4B5563]">
          <p>
            All APC English tuition is English <span className="font-semibold text-[#1B2A41]">Home Language</span>; all Afrikaans is <span className="font-semibold text-[#1B2A41]">First Additional Language</span>. CAPS and IEB streams are kept separate; IEB does not include Economics.
          </p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────── BENTO: WHY APC ───────────────────────────
const Bento = () => {
  const cells = [
    { icon: Globe, title: 'Nationwide, online', body: 'Join live sessions from anywhere in South Africa.', wide: true, dark: true },
    { icon: Calculator, title: 'CAPS-aligned', body: 'Lessons built to the CAPS curriculum, grade by grade.' },
    { icon: BookText, title: 'IEB subject offerings', body: 'A dedicated IEB stream for Grades 8–12.' },
    { icon: FlaskConical, title: 'Qualified tutors', body: 'Capable, exam-focused specialists lead every subject.' },
    { icon: TrendingUp, title: 'Flexible monthly', body: 'No long-term contracts. Enrol per subject, per month.' },
  ];
  return (
    <section className="section-pad bg-white" data-testid="bento-section">
      <div className="container-apc">
        <div className="overline mb-3">Why APC</div>
        <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A41] max-w-3xl mb-12">
          Structured academic support, <span className="font-editorial font-normal text-[#1F6F4A]">built around</span> the way kids actually learn.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5 auto-rows-[minmax(180px,auto)]">
          {cells.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }}
              className={`rounded-3xl p-6 md:p-8 flex flex-col justify-between ${
                c.dark ? 'bg-[#1F6F4A] text-white md:col-span-4 md:row-span-2' : 'bg-[#F3F5F2] text-[#1B2A41] md:col-span-2'
              }`}
              data-testid={`bento-cell-${i}`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.dark ? 'bg-white/15' : 'bg-white'}`}>
                <c.icon size={22} className={c.dark ? 'text-white' : 'text-[#1F6F4A]'} />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>{c.title}</h3>
                <p className={`text-sm md:text-base ${c.dark ? 'text-white/80' : 'text-[#4B5563]'}`}>{c.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────── TUTOR SPOTLIGHT ───────────────────────────
const TutorSpotlight = () => (
  <section id="tutors" className="section-pad bg-[#1B2A41] text-white overflow-hidden" data-testid="tutor-section">
    <div className="container-apc grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5 order-2 lg:order-1">
        <div className="overline mb-4">The Faculty</div>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
          Meet the <span className="font-editorial font-normal text-[#D4A017]">tutor</span> behind APC.
        </h2>
        <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
          A qualified educator and exam-performance specialist with the credentials and
          classroom instinct to guide South African learners to real improvement.
        </p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {[
            'Qualified Educator', 'B.Ed', 'BCom Honours Economics', 'MSc in AI (UJ, in progress)',
          ].map((c) => (
            <div key={c} className="bg-white/5 rounded-xl px-4 py-3 text-sm backdrop-blur-sm border border-white/10">
              <Check size={14} className="text-[#D4A017] inline mr-1.5"/> {c}
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-7 order-1 lg:order-2">
        <div className="relative">
          <div className="spotlight-img aspect-[4/3] max-w-xl ml-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_exam-results-boost/artifacts/kl79ywh9_unnamed%20%281%29.jpg"
              alt="Tutor leading a live lesson"
              className="w-full h-full object-cover"
              data-testid="tutor-image"
            />
          </div>
          <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8 bg-[#FAF9F6] text-[#1B2A41] rounded-2xl px-5 py-4 shadow-xl">
            <p className="text-[11px] font-mono tracking-wider text-[#4B5563]">LEAD TUTOR</p>
            <p className="font-bold">Mr. R</p>
            <p className="text-xs text-[#4B5563]">Exam performance specialist</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────── PRICING ───────────────────────────
const Pricing = () => {
  const tiers = [
    { label: 'Foundation', band: 'Grades 4 – 11', price: 'R99', freq: '2 live sessions / week · per subject', streams: 'CAPS' },
    { label: 'Matric', band: 'Grade 12', price: 'R199', freq: '2 live sessions / week · per subject', streams: 'CAPS', highlight: true },
    { label: 'IEB stream', band: 'Grades 8 – 12', price: 'R249', freq: '2 live sessions / week · per subject', streams: 'IEB' },
  ];
  return (
    <section id="pricing" className="section-pad bg-[#FAF9F6]" data-testid="pricing-section">
      <div className="container-apc">
        <div className="overline mb-3">Pricing</div>
        <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A41] max-w-3xl mb-12">
          Per-subject, per-month. <span className="font-editorial font-normal text-[#1F6F4A]">No contracts.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <div
              key={t.label}
              className={`rounded-3xl p-7 md:p-8 flex flex-col ${
                t.highlight ? 'bg-[#1F6F4A] text-white' : 'bg-white text-[#1B2A41] border border-black/5'
              }`}
              data-testid={`pricing-tier-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <p className={`text-xs font-mono tracking-wider mb-5 ${t.highlight ? 'text-[#D4A017]' : 'text-[#D4A017]'}`}>
                {t.streams} · {t.label.toUpperCase()}
              </p>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>{t.band}</h3>
              <div className="flex items-baseline gap-1 my-4">
                <span className={`text-5xl md:text-6xl font-black`}>{t.price}</span>
                <span className={`${t.highlight ? 'text-white/70' : 'text-[#4B5563]'} text-sm`}>/ subject / mo</span>
              </div>
              <p className={`${t.highlight ? 'text-white/80' : 'text-[#4B5563]'} text-sm mb-8`}>{t.freq}</p>
              <Link to="/register" className={`btn-pill ${t.highlight ? 'btn-gold' : 'btn-primary'} mt-auto w-full justify-center`} data-testid={`pricing-cta-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
                Register for 2027 <ArrowRight size={16}/>
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#4B5563]">Prices are per subject, per month — not for all subjects combined. Enrol in as many as needed.</p>
      </div>
    </section>
  );
};

// ─────────────────────────── FAQ ───────────────────────────
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const items = [
    { q: 'How do live classes work?', a: 'Each class is a live online lesson delivered on Zoom at scheduled times. All sessions are recorded and uploaded to Google Classroom so learners can rewatch anytime.' },
    { q: 'How many lessons per subject?', a: 'Every subject, at every grade, receives 2 live online sessions per week — across both CAPS and IEB. That is approximately 8 live sessions per subject per month, giving learners consistent structured contact with their tutors.' },
    { q: 'What are the fees?', a: 'Fees are per subject per month: R99 for CAPS Grades 4–11, R199 for CAPS Grade 12, and R249 for all IEB subjects. Enrol in as many subjects as your child needs — the price is per subject, not for all subjects combined.' },
    { q: 'Can one parent register multiple children?', a: 'Yes. One parent can register multiple learners in a single process; each learner is kept as a separate record, linked to the parent/guardian.' },
    { q: 'What is the cancellation policy?', a: 'No long-term contracts. Cancel anytime via WhatsApp or email.' },
    { q: 'What devices are needed?', a: 'Any device with internet access — smartphone, tablet, laptop or desktop. A larger screen is recommended but phones are fully supported.' },
    { q: 'How do payments work?', a: 'APC uses direct bank transfer. After you register you receive a reference number and payment instructions. Access to Google Classroom is granted once your payment is received and verified.' },
  ];
  return (
    <section id="faq" className="section-pad bg-white" data-testid="faq-section">
      <div className="container-apc max-w-4xl">
        <div className="overline mb-3">FAQ</div>
        <h2 className="text-3xl md:text-5xl font-bold text-[#1B2A41] mb-12">
          Questions, <span className="font-editorial font-normal text-[#1F6F4A]">answered.</span>
        </h2>
        <div className="divide-y divide-black/10">
          {items.map((it, i) => (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full py-6 flex items-center justify-between text-left group"
              >
                <span className="text-lg md:text-xl font-semibold text-[#1B2A41] pr-4 group-hover:text-[#1F6F4A] transition-colors" style={{ fontFamily: 'Outfit' }}>
                  {it.q}
                </span>
                <motion.span animate={{ rotate: open === i ? 45 : 0 }} className="shrink-0 w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#1F6F4A] font-bold text-xl">
                  +
                </motion.span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 text-[#4B5563] leading-relaxed max-w-2xl">{it.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────── FINAL CTA ───────────────────────────
const FinalCTA = () => (
  <section className="section-pad bg-[#1F6F4A] text-white relative overflow-hidden" data-testid="final-cta-section">
    <div className="absolute inset-0 -z-0 pointer-events-none">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#D4A017]/15 blur-3xl" />
    </div>
    <div className="container-apc relative text-center max-w-3xl">
      <div className="overline mb-4 text-[#D4A017]">January 2027 intake</div>
      <h2 className="text-4xl md:text-6xl font-black leading-[1.05] mb-8">
        Give your child <span className="font-editorial font-normal text-[#D4A017]">structured</span> support — before results day.
      </h2>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/register" className="btn-pill btn-gold text-base" data-testid="final-register-cta">
          Register for 2027 <ArrowRight size={18} />
        </Link>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-pill border-2 border-white/40 text-white hover:bg-white hover:text-[#1F6F4A] transition-all" data-testid="final-whatsapp-cta">
          <MessageCircle size={16}/> Message APC on WhatsApp
        </a>
      </div>
    </div>
  </section>
);

// ─────────────────────────── FOOTER ───────────────────────────
const Footer = () => (
  <footer className="bg-[#1B2A41] text-white py-14" data-testid="footer">
    <div className="container-apc">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-2.5 mb-5" data-testid="footer-logo">
            <ApcMark size={34} />
            <span className="font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>Accounting Prep Club</span>
          </Link>
          <p className="text-white/60 max-w-md text-sm leading-relaxed">
            Structured online academic support for Grade 4–12 learners — live online lessons across
            Mathematics, Accounting, Sciences, Languages, Economics and more. CAPS and IEB streams.
          </p>
          <div className="flex gap-3 mt-6">
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#1F6F4A] transition-colors" aria-label="Facebook" data-testid="social-facebook"><Facebook size={16}/></a>
            <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#1F6F4A] transition-colors" aria-label="TikTok" data-testid="social-tiktok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
            </a>
            <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#1F6F4A] transition-colors" aria-label="YouTube" data-testid="social-youtube"><Youtube size={16}/></a>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-xs font-mono tracking-wider text-[#D4A017] mb-4">EXPLORE</p>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href="#offering" className="hover:text-white">Offering</a></li>
            <li><a href="#tutors" className="hover:text-white">Faculty</a></li>
            <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
            <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            <li><Link to="/register" className="hover:text-white">Register for 2027</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="text-xs font-mono tracking-wider text-[#D4A017] mb-4">CONTACT</p>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail size={14}/> {CONTACT_EMAIL}</a></li>
            <li><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-whatsapp"><Phone size={14}/> {CONTACT_PHONE_DISPLAY}</a></li>
          </ul>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full mt-5 text-sm font-medium hover:bg-[#20bd5a]" data-testid="footer-whatsapp-cta">
            <MessageCircle size={14}/> Chat on WhatsApp
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/40">
        <p>© {new Date().getFullYear()} The Accounting Prep Club. All rights reserved.</p>
        <p>CAPS & IEB tuition · POPIA-aligned processing of parent and learner data.</p>
      </div>
    </div>
  </footer>
);

// ─────────────────────────── MAIN ───────────────────────────
const LandingPage = () => {
  useLenis();
  return (
    <div className="min-h-screen bg-[#FAF9F6] grain" data-testid="landing-page">
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Offering />
        <Bento />
        <TutorSpotlight />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
